import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {
  HardwareManagementSubnetzmaskeStore,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_SUBNETZMASKE
} from '../../../hardware-management.shared';
import {map} from 'rxjs/operators';
import {UntypedFormControl, UntypedFormGroup, FormGroupDirective, ReactiveFormsModule} from '@angular/forms';
import {
  MessageBarStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  Validators,
} from '@softline/ui-core';
import { EntityInputComponent } from '@softline/ui-core/entity-input';
import {FocusMonitor} from '@angular/cdk/a11y';
import {Store} from '@softline/core';
import {showRequestErrors} from '@softline/application';
import {Subnetzmaske} from '../../../types/subnetzmaske';
import {CommonModule} from '@angular/common';
import {InputBaseComponent} from '../../input-base/input-base.component';

import { InputComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    selector: 'lib-subnetzmaske-input',
    imports: [CommonModule, InputBaseComponent, ReactiveFormsModule,
    InputComponent,
    TranslatePipe],
    templateUrl: './subnetzmaske-input.component.html',
    styleUrls: ['./subnetzmaske-input.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubnetzmaskeInputComponent {

  @Input() readonly = false;

  readonly subnetzmaskeInEditing$ = new BehaviorSubject<Subnetzmaske | null>(null);

  readonly entities$ = this.store.observe(
    SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_SUBNETZMASKE,
    HardwareManagementSubnetzmaskeStore.getters.all
  ).pipe(map(o => o as Subnetzmaske[]));

  get formGroup(): UntypedFormGroup {
    return this.parentFormGroup.form;
  }

  readonly subnetzmaskeEditingForm = new UntypedFormGroup({
    subnetzmaske: new UntypedFormControl(null, Validators.required()),
    bezeichnung: new UntypedFormControl(null),
    beschreibung: new UntypedFormControl(null)
  });

  constructor(
    private readonly parentFormGroup: FormGroupDirective,
    private readonly focusMonitor: FocusMonitor,
    private store: Store
  ) { }

  select(subnetzmaske: Subnetzmaske | null | undefined): void {
    if(!subnetzmaske)
      return;
    this.formGroup.patchValue({ subnetzmaske });
  }

  edit(entity: Subnetzmaske | null | undefined): void {
    if(!entity)
      return;
    this.subnetzmaskeInEditing$.next(entity);
    this.subnetzmaskeEditingForm.patchValue(entity);
  }

  resetForm(subnetzmaske?: Subnetzmaske | null): void {
    if (!subnetzmaske) {
      this.subnetzmaskeEditingForm.reset();
      return;
    }

    this.subnetzmaskeEditingForm.reset(subnetzmaske);
  }

  async saveEdit(entity: Subnetzmaske): Promise<void> {
    const updatedEntity = {
      ...entity,
      ...this.subnetzmaskeEditingForm.value
    };

    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_SUBNETZMASKE,
        HardwareManagementSubnetzmaskeStore.actions.update,
        { entity: updatedEntity }
      );

      this.select(result);
      this.subnetzmaskeEditingForm.reset();
      this.subnetzmaskeInEditing$.next(null);

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Subnetzmaske wurde erfolgreich aktualisiert'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async create(entity: Subnetzmaske): Promise<void> {
    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_SUBNETZMASKE,
        HardwareManagementSubnetzmaskeStore.actions.create,
        { entity }
      );

      this.select(result);
      this.subnetzmaskeEditingForm.reset();

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Subnetzmaske wurde erfolgreich angelegt'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  valueChanged(newValue: Subnetzmaske | null | undefined): void {
    if (!newValue) {
      this.subnetzmaskeEditingForm.reset();
      this.subnetzmaskeInEditing$.next(null);
    }

    if (newValue && this.formGroup.value?.subnetzmaske?.id !== newValue?.id) {
      this.formGroup.patchValue({ subnetzmaske: newValue }, { onlySelf: true });
    }
  }

  canceled(entityInput?: EntityInputComponent<any>): void {
    this.formGroup.patchValue({ subnetzmaske: null });
    this.subnetzmaskeEditingForm.reset();
    this.subnetzmaskeInEditing$.next(null);

    if (entityInput) {
      // remove any existing text of the input (reset)
      entityInput.onInput('');
      entityInput.input = '';
      entityInput.inputComponent.nativeElement.value = '';
    }
  }

  closed(): void {
    this.subnetzmaskeEditingForm.reset();
  }

  // We have to use an arrow-function in order to preserve `this` context
  readonly filterTypes = (types: Subnetzmaske[], filter: string): Subnetzmaske[] => {
    if (!filter || filter?.length < 1) {
      return types;
    }

    return types.filter(typ => {
      return typ.subnetzmaske.toLowerCase().includes(filter)
        || typ.bezeichnung.toLowerCase().includes(filter)
        || typ.beschreibung.toLowerCase().includes(filter);
    });
  }
}
